import { zodResolver } from '@hookform/resolvers/zod';
import type { ValidationErrorResponse } from '@wyz/types';
import {
  Button,
  Card,
  Col,
  Flex,
  FormContainer,
  FormFeedback,
  Icon,
  IconButton,
  Row,
  Title,
} from '@wyz/ui';
import { isNil } from 'lodash-es';
import * as React from 'react';
import {
  UseFormGetFieldState,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { Link } from 'react-router';
import { Trans, useI18n } from '~/common/context';
import { CommonErrorCodesEnumType, TranslationKeys } from '~/common/types';
import {
  AlphabeticalListSearchInput,
  SupplierAlphabeticalList,
  SupplierDraggableItem,
  SupplierDraggables,
  type SupplierItem,
} from '~/connect/components';

import {
  CriteriaBuilder,
  MatchedCustomersTable,
  TextField,
} from '~/common/components';
import { FEATURE_CONTEXT } from '~/common/schemas';
import { WheelSupplierTemplateUtils } from '~/connect/helpers';
import { useScrollToNewItem } from '~/connect/hooks';
import {
  SUPPLIERS_STATUSES,
  wheelSupplierTemplateFormSchema,
} from '~/connect/schemas';
import {
  IAnonymizedSupplierPlatform,
  IWheelSupplierTemplateForm,
} from '~/connect/types';
import { useBackTo, useValidationForm } from '~/common/hooks';

type WheelSupplierTemplateFormProps =
  | {
      onSubmit: (
        formData: IWheelSupplierTemplateForm,
        formState: {
          criteria: ReturnType<
            UseFormGetFieldState<IWheelSupplierTemplateForm>
          >;
        },
      ) => void;
      isLoading: boolean;
      defaultValues?: IWheelSupplierTemplateForm;
      errors: ValidationErrorResponse<CommonErrorCodesEnumType> | null;
      mode?: 'CREATE' | 'UPDATE';
      anonymizedSuppliers: IAnonymizedSupplierPlatform[];
    }
  | {
      mode?: 'READ_ONLY';
      defaultValues?: IWheelSupplierTemplateForm;
      isLoading?: never;
      onSubmit?: never;
      errors?: never;
      anonymizedSuppliers: IAnonymizedSupplierPlatform[];
    };

const DEFAULT_VALUES: IWheelSupplierTemplateForm = {
  name: '',
  suppliers: [],
  criteria: [],
};

export const WheelSupplierTemplateForm = ({
  anonymizedSuppliers,
  errors,
  defaultValues = DEFAULT_VALUES,
  isLoading,
  onSubmit,
  mode = 'CREATE',
}: WheelSupplierTemplateFormProps) => {
  const backTo = useBackTo('/platform/wheels/supplier-templates');
  const { t } = useI18n();

  const methods = useForm<IWheelSupplierTemplateForm>({
    resolver: zodResolver(wheelSupplierTemplateFormSchema),
    defaultValues: defaultValues,
  });
  const { remove, insert, fields, append } = useFieldArray<
    IWheelSupplierTemplateForm,
    'suppliers',
    'key'
  >({
    control: methods.control,
    name: 'suppliers',
    keyName: 'key',
  });

  useValidationForm({
    methods,
    errors: errors?.errors,
    t,
  });
  const handleMove = React.useCallback(
    (from: number, to: number) => {
      const currentSuppliers = methods.getValues('suppliers');
      const value = currentSuppliers[from];
      remove(from);
      insert(to, value);
    },
    [insert, methods, remove],
  );
  const handleOnAddSupplier = ({
    supplierPlatform,
  }: IAnonymizedSupplierPlatform) => {
    const isNewSupplier = isNil(
      fields.find(
        (supplier) => supplier.wyzApiId === supplierPlatform.supplier.id,
      ),
    );
    if (isNewSupplier) {
      append({
        ...supplierPlatform.supplier,
        type: supplierPlatform.supplier.supplierType.id,
        status: SUPPLIERS_STATUSES.DISABLED,
        enabled: false,
        orderable: true,
        wyzApiId: supplierPlatform.supplier.id,
      });
    }
  };
  const handleOnRemove = (id: number) => {
    const index = fields.findIndex((supplier) => supplier.wyzApiId === id);
    remove(index);
  };

  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [configuredSupplier, setConfiguredSupplier] = React.useState<
    SupplierItem | undefined
  >();
  const watchedSuppliers = useWatch({
    name: 'suppliers',
    control: methods.control,
  });
  const configuredSupplierIndex = configuredSupplier
    ? watchedSuppliers.findIndex(
        (field) => field.wyzApiId === configuredSupplier.wyzApiId,
      )
    : undefined;

  useScrollToNewItem(fields, 'supplier-name', 'wyzApiId');

  const groupedByLetter =
    WheelSupplierTemplateUtils.groupByLetter(anonymizedSuppliers);

  return (
    <Row>
      <Col sm={12} md={12}>
        <FormContainer
          methods={methods}
          submitHandler={(formData) => {
            if (mode === 'CREATE' || !methods.formState.isDirty) {
              onSubmit?.(formData, {
                criteria: methods.getFieldState('criteria'),
              });
            } else if (mode === 'UPDATE') {
              onSubmit?.(formData, {
                criteria: methods.getFieldState('criteria'),
              });
            }
          }}
          formConfiguration={{
            mode: mode === 'READ_ONLY' ? 'READ_ONLY' : 'WRITE',
          }}
        >
          <Card className='mb-section'>
            <Card.Header>
              <Title heading='lg' color='tertiary' weight='bold'>
                <Icon icon='post' width='1.5rem' height='1.5rem' />
                <span className='ms-2'>
                  <Trans i18nKey='app.common.general_information' />
                </span>
              </Title>
            </Card.Header>
            <Card.Body>
              <Row>
                <TextField
                  required
                  name='name'
                  label={<Trans i18nKey='app.common.model_name' />}
                />
              </Row>
            </Card.Body>
          </Card>
          {/* Criteria Section */}
          <Card className='mb-section'>
            <Card.Header className='pb-0'>
              <Title heading='lg' color='tertiary' weight='bold'>
                <Icon icon='clip-board' width='1.5rem' height='1.5rem' />
                <span className='ms-2'>
                  <Trans i18nKey='app.common.criteria' />
                </span>
              </Title>
            </Card.Header>
            <Card.Body>
              <CriteriaBuilder>
                <Row className='mt-4'>
                  <Col md={6}>
                    <MatchedCustomersTable
                      featureContext={FEATURE_CONTEXT.WHEEL_SUPPLIER_TEMPLATE}
                      name={'criteria'}
                    />
                  </Col>
                </Row>
              </CriteriaBuilder>
            </Card.Body>
          </Card>

          {/* Suppliers Section  */}
          <Card className='mb-section'>
            <Card.Header className='pb-0'>
              <Title heading='lg' color='tertiary' weight='bold'>
                <Icon icon='team' width='1.5rem' height='1.5rem' />
                <span className='ms-2'>
                  <Trans i18nKey='app.supplier_template.form.suppliers.title' />
                </span>
              </Title>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col
                  md={configuredSupplier ? 3 : 7}
                  className={configuredSupplier ? 'opacity-25' : undefined}
                >
                  <AlphabeticalListSearchInput
                    value={searchQuery}
                    label={
                      <Trans i18nKey='app.supplier_template.form.suppliers.search_label' />
                    }
                    placeholder='Ex : BRIDGE W2S NISSAN FR, CHRONO LIMOGES,...'
                    onChange={setSearchQuery}
                  />
                  <SupplierAlphabeticalList
                    currentTab={-1}
                    itemsByTab={groupedByLetter}
                    onAdd={handleOnAddSupplier}
                    onRemove={handleOnRemove}
                    searchQuery={searchQuery}
                  />
                </Col>
                <Col
                  md={5}
                  className='position-relative'
                  style={{ borderLeft: '1px solid var(--neutral-grey-4)' }}
                >
                  {configuredSupplier && (
                    <div
                      className='position-absolute'
                      style={{ right: '105%' }}
                    >
                      <IconButton
                        id='close-configuration'
                        icon='arrow-left-s-line'
                        variant='secondary'
                        onClick={() => setConfiguredSupplier(undefined)}
                        rounded
                      />
                    </div>
                  )}
                  <Title heading='lg' weight='bold'>
                    <Trans
                      i18nKey='app.supplier_template.form.suppliers.order_title'
                      values={{
                        count: fields.length,
                      }}
                    />
                  </Title>
                  <SupplierDraggables
                    featureContext={FEATURE_CONTEXT.WHEEL_SUPPLIER_TEMPLATE}
                    onMove={handleMove}
                    suppliers={watchedSuppliers}
                    onConfigure={(supplier) => {
                      setConfiguredSupplier(supplier);
                    }}
                    onRemove={handleOnRemove}
                    onConfigureBestSourcing={() => {}}
                  />
                  {methods.formState.errors.suppliers?.message && (
                    <FormFeedback invalid id='suppliers.error'>
                      <Trans
                        i18nKey={
                          methods.formState.errors.suppliers
                            ?.message as keyof TranslationKeys
                        }
                      />
                    </FormFeedback>
                  )}
                </Col>
                {!isNil(configuredSupplier) &&
                  !isNil(configuredSupplierIndex) && (
                    <Col
                      key={configuredSupplierIndex}
                      md={4}
                      style={{ borderLeft: '1px solid var(--neutral-grey-4)' }}
                    >
                      <Title heading='lg' weight='bold' wrap>
                        <Trans
                          i18nKey='app.supplier_template.form.suppliers.configure_supplier'
                          values={{
                            supplierName: configuredSupplier.name,
                          }}
                        />
                      </Title>
                      <SupplierDraggableItem
                        baseName={`suppliers.${configuredSupplierIndex}`}
                        supplier={{
                          id: configuredSupplier.wyzApiId,
                          type: configuredSupplier.type,
                        }}
                        index={configuredSupplierIndex}
                        display={{
                          overridePriorityIfShorterDelivery: false,
                          orderable: true,
                          rankingModel: false,
                          extendedManufConfiguration: false,
                        }}
                      />
                    </Col>
                  )}
              </Row>
            </Card.Body>
          </Card>

          {/* Action */}
          {mode !== 'READ_ONLY' && (
            <Card className='mt-3'>
              <Card.Body>
                <Flex className='space-x-4' justifyContent='between'>
                  <Button
                    id='cancel'
                    as={Link}
                    variant='secondary'
                    type='button'
                    to={backTo}
                  >
                    <Trans i18nKey='app.common.button.cancel' />
                  </Button>
                  <Button
                    id='submit'
                    variant='primary'
                    type='submit'
                    disabled={isLoading}
                  >
                    <Trans
                      i18nKey={
                        mode === 'CREATE'
                          ? 'app.supplier_template.form.create_submit_btn'
                          : 'app.supplier_template.form.edit_submit_btn'
                      }
                    />
                  </Button>
                </Flex>
              </Card.Body>
            </Card>
          )}
        </FormContainer>
      </Col>
    </Row>
  );
};
